import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, X, AlertCircle, RefreshCw, Zap } from 'lucide-react';

// ── Nostr-native GIF Picker ──────────────────────────────────────
//
// Searches for GIFs using NIP-94 (kind 1063) file metadata events
// stored on Nostr relays via NIP-50 search. Falls back to GIPHY v1
// when the Nostr GIF library doesn't have enough results.

const PAGE_SIZE = 30;

// Relays that support NIP-50 search and have GIF content
const SEARCH_RELAYS = [
  'wss://relay.nostr.band',
  'wss://search.nos.today',
];

// GIPHY public SDK key (v1 API — works cross-origin without proxy)
const GIPHY_KEY = 'Gc7131jiJuvI7IdN0HZ1D7nh0ow5BU6g';

const CATEGORIES = [
  'Trending', 'Bitcoin', 'Money', 'Celebrate',
  'Crypto', 'Laugh', 'Thumbs Up', 'Dance',
  'Wow', 'Sad', 'Yes', 'No',
];

interface GifResult {
  id: string;
  url: string;        // Full-quality URL to insert in post
  thumb?: string;      // Small preview URL for grid
  alt?: string;
}

interface GifPickerProps {
  onSelect: (url: string) => void;
  onClose: () => void;
}

// ── NIP-94 parsing ──────────────────────────────────────────────

function parseNip94Event(event: any): GifResult | null {
  if (!event?.tags) return null;

  const tagMap = new Map<string, string>();
  for (const tag of event.tags) {
    if (tag.length >= 2 && !tagMap.has(tag[0])) {
      tagMap.set(tag[0], tag[1]);
    }
  }

  const url = tagMap.get('url');
  if (!url) return null;

  const mime = tagMap.get('m') || '';
  // Only include GIFs and animated content
  const isGif = mime === 'image/gif' || mime === 'image/webp' || mime === 'video/mp4'
    || /\.gif(\?|$)/i.test(url)
    || /giphy|tenor|gif/i.test(url);

  if (!isGif) return null;

  return {
    id: event.id || url,
    url,
    thumb: tagMap.get('thumb') || tagMap.get('image') || undefined,
    alt: tagMap.get('alt') || event.content || undefined,
  };
}

// ── Nostr relay search (NIP-50 + kind 1063) ─────────────────────

function searchNostrGifs(query: string, limit: number = PAGE_SIZE): Promise<GifResult[]> {
  return new Promise((resolve) => {
    const results = new Map<string, GifResult>();
    const sockets: WebSocket[] = [];
    let closedCount = 0;
    let resolved = false;

    const finish = () => {
      if (resolved) return;
      resolved = true;
      for (const ws of sockets) {
        try { ws.close(); } catch {}
      }
      resolve(Array.from(results.values()));
    };

    // Don't wait forever — 6s max
    const timeout = setTimeout(finish, 6000);

    for (const relayUrl of SEARCH_RELAYS) {
      try {
        const ws = new WebSocket(relayUrl);
        sockets.push(ws);

        const checkDone = () => {
          closedCount++;
          if (closedCount >= sockets.length) {
            clearTimeout(timeout);
            finish();
          }
        };

        ws.onopen = () => {
          const subId = 'gif_' + Math.random().toString(36).slice(2, 8);
          ws.send(JSON.stringify(['REQ', subId, {
            kinds: [1063],
            limit,
            search: query,
          }]));
        };

        ws.onmessage = (msg) => {
          try {
            const data = JSON.parse(msg.data);
            if (data[0] === 'EVENT') {
              const gif = parseNip94Event(data[2]);
              if (gif && !results.has(gif.url)) {
                results.set(gif.url, gif);
              }
            } else if (data[0] === 'EOSE' || data[0] === 'CLOSED') {
              checkDone();
            }
          } catch {}
        };

        ws.onerror = () => checkDone();
        ws.onclose = () => {}; // already handled by EOSE/error
      } catch {
        closedCount++;
      }
    }

    if (sockets.length === 0) {
      clearTimeout(timeout);
      resolve([]);
    }
  });
}

// ── GIPHY v1 API (fallback — works cross-origin natively) ───────

interface GiphyImage {
  url?: string;
  width?: string;
  height?: string;
}

function giphyImageUrl(img?: GiphyImage): string | undefined {
  return img?.url || undefined;
}

async function searchGiphyGifs(
  query: string,
  limit: number = PAGE_SIZE,
  offset: number = 0
): Promise<{ results: GifResult[]; total: number }> {
  const isTrending = query.toLowerCase() === 'trending';
  const url = isTrending
    ? `https://api.giphy.com/v1/gifs/trending?api_key=${GIPHY_KEY}&limit=${limit}&offset=${offset}&rating=g`
    : `https://api.giphy.com/v1/gifs/search?api_key=${GIPHY_KEY}&q=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}&rating=g`;

  const res = await fetch(url, { signal: AbortSignal.timeout(8000) });
  if (!res.ok) throw new Error(`GIPHY ${res.status}`);
  const data = await res.json();

  const results: GifResult[] = (data.data || []).map((item: any) => {
    const images = item.images || {};
    return {
      id: item.id,
      url: giphyImageUrl(images.downsized_medium) || giphyImageUrl(images.original) || giphyImageUrl(images.downsized) || '',
      thumb: giphyImageUrl(images.fixed_width_small) || giphyImageUrl(images.fixed_width) || giphyImageUrl(images.downsized) || '',
      alt: item.title || '',
    };
  }).filter((g: GifResult) => g.url);

  return {
    results,
    total: data.pagination?.total_count || results.length,
  };
}

// ── Component ───────────────────────────────────────────────────

export default function GifPicker({ onSelect, onClose }: GifPickerProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('Trending');
  const [gifs, setGifs] = useState<GifResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [source, setSource] = useState<'nostr' | 'giphy'>('nostr');
  const [giphyOffset, setGiphyOffset] = useState(0);
  const [giphyTotal, setGiphyTotal] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce search input (400ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 400);
    return () => clearTimeout(timer);
  }, [query]);

  // ── Main search: Nostr first, GIPHY fallback ─────────────────
  const fetchGifs = useCallback(async (searchQuery: string, loadMore: boolean = false) => {
    if (loadMore) setLoadingMore(true); else setLoading(true);
    setError('');

    try {
      if (!loadMore) {
        // ── Fresh search ────────────────────────────────────────
        // Try Nostr relays first (non-blocking — race with a short timeout)
        let nostrResults: GifResult[] = [];
        try {
          nostrResults = await searchNostrGifs(searchQuery);
        } catch {
          // Nostr search failed silently — that's fine
        }

        if (nostrResults.length >= 8) {
          // Enough Nostr-native GIFs!
          setGifs(nostrResults);
          setSource('nostr');
          setGiphyOffset(0);
          setGiphyTotal(0);
          scrollRef.current?.scrollTo({ top: 0 });
        } else {
          // Fall back to GIPHY (merge any Nostr results at the top)
          const giphy = await searchGiphyGifs(searchQuery);
          const all = [...nostrResults];
          for (const gif of giphy.results) {
            if (!all.some(g => g.url === gif.url)) {
              all.push(gif);
            }
          }
          setGifs(all);
          setSource(nostrResults.length > 0 ? 'nostr' : 'giphy');
          setGiphyOffset(giphy.results.length);
          setGiphyTotal(giphy.total);
          scrollRef.current?.scrollTo({ top: 0 });
        }
      } else {
        // ── Load more (GIPHY pagination) ────────────────────────
        if (giphyOffset < giphyTotal) {
          const giphy = await searchGiphyGifs(searchQuery, PAGE_SIZE, giphyOffset);
          setGifs(prev => {
            const all = [...prev];
            for (const gif of giphy.results) {
              if (!all.some(g => g.url === gif.url)) {
                all.push(gif);
              }
            }
            return all;
          });
          setGiphyOffset(prev => prev + giphy.results.length);
          setGiphyTotal(giphy.total);
        }
      }
    } catch (e: any) {
      console.warn('GIF fetch failed:', e);
      if (!loadMore) setGifs([]);
      setError(e?.message || 'Failed to load GIFs. Check your connection and try again.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [giphyOffset, giphyTotal]);

  // Trigger fetch when search or category changes
  useEffect(() => {
    const searchTerm = debouncedQuery || activeCategory.toLowerCase();
    fetchGifs(searchTerm, false);
  }, [debouncedQuery, activeCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll
  const hasMore = giphyOffset < giphyTotal;
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || loadingMore || !hasMore) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
      const searchTerm = debouncedQuery || activeCategory.toLowerCase();
      fetchGifs(searchTerm, true);
    }
  }, [loadingMore, hasMore, debouncedQuery, activeCategory, fetchGifs]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Escape to close
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const selectCategory = (cat: string) => {
    setActiveCategory(cat);
    setQuery('');
    setDebouncedQuery('');
  };

  const handleSelect = (gif: GifResult) => {
    if (gif.url) onSelect(gif.url);
  };

  return (
    <div className="mt-2 border border-border rounded-xl overflow-hidden bg-card shadow-lg">
      {/* Header: search + close */}
      <div className="p-2 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              value={query}
              onChange={e => { setQuery(e.target.value); setActiveCategory(''); }}
              placeholder="Search GIFs..."
              className="w-full bg-muted rounded-lg pl-3 pr-8 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              autoFocus
            />
            {query && (
              <button
                onClick={() => { setQuery(''); setDebouncedQuery(''); setActiveCategory('Trending'); inputRef.current?.focus(); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Category pills */}
        <div className="flex gap-1 mt-2 overflow-x-auto scrollbar-none pb-0.5">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => selectCategory(cat)}
              className={'px-2.5 py-1 rounded-full text-xs whitespace-nowrap transition-colors ' + (
                activeCategory === cat && !query
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              )}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* GIF grid */}
      <div ref={scrollRef} className="max-h-72 overflow-y-auto overscroll-contain">
        {loading && (
          <div className="grid grid-cols-3 gap-1 p-1">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="aspect-square bg-muted rounded-lg animate-pulse" />
            ))}
          </div>
        )}

        {!loading && error && gifs.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 px-4 gap-2">
            <AlertCircle className="w-8 h-8 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground text-center">{error}</p>
            <button
              onClick={() => fetchGifs(debouncedQuery || activeCategory.toLowerCase())}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
            >
              <RefreshCw className="w-3 h-3" /> Try Again
            </button>
          </div>
        )}

        {!loading && !error && gifs.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 px-4 gap-1">
            <p className="text-sm font-medium text-muted-foreground">No GIFs found</p>
            <p className="text-xs text-muted-foreground/70">Try a different search term</p>
          </div>
        )}

        {!loading && gifs.length > 0 && (
          <div className="grid grid-cols-3 gap-1 p-1">
            {gifs.map((gif) => {
              const displayUrl = gif.thumb || gif.url;
              if (!displayUrl) return null;
              return (
                <button
                  key={gif.id}
                  onClick={() => handleSelect(gif)}
                  className="aspect-square overflow-hidden rounded-lg hover:opacity-80 active:scale-95 transition-all relative bg-muted group"
                  title={gif.alt || 'GIF'}
                >
                  <img
                    src={displayUrl}
                    alt={gif.alt || ''}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </button>
              );
            })}
          </div>
        )}

        {loadingMore && (
          <div className="flex items-center justify-center py-3">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
          </div>
        )}

        {!loading && !loadingMore && hasMore && gifs.length > 0 && (
          <div className="flex items-center justify-center py-2 pb-3">
            <button
              onClick={() => {
                const searchTerm = debouncedQuery || activeCategory.toLowerCase();
                fetchGifs(searchTerm, true);
              }}
              className="px-4 py-1.5 rounded-lg bg-muted text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors"
            >
              Load More
            </button>
          </div>
        )}
      </div>

      {/* Attribution */}
      <div className="px-2 py-1.5 border-t border-border flex items-center justify-center gap-1.5">
        {source === 'nostr' ? (
          <span className="flex items-center gap-1 text-[9px] text-muted-foreground/50">
            <Zap className="w-2.5 h-2.5" /> Powered by Nostr (NIP-94)
          </span>
        ) : (
          <span className="text-[9px] text-muted-foreground/50">Powered by GIPHY</span>
        )}
      </div>
    </div>
  );
}

